import { useContext, useState } from 'react';
import { MailIcon, MapIcon, PhoneCallIcon } from 'lucide-react';
import emailjs from '@emailjs/browser';
import { DetailsContext } from '@/context/DetailContext';

export const ContactUS = () => {
  const { details } = useContext(DetailsContext);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [status, setStatus] = useState('idle'); // idle | sending | success | error

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.placeholder.toLowerCase()]: e.target.value });
  };

  const handleSend = async () => {
    if (!formData.name || !formData.email || !formData.message) {
      alert('Please fill all required fields.');
      return;
    }

    setStatus('sending');

    const templateParams = {
      name: formData.name,
      email: formData.email,
      subject: formData.subject,
      message: formData.message,
      year: new Date().getFullYear(),
      to_email: details.email,
      heading: '📩 New Contact Message',
    };

    try {
      await emailjs.send(
        'service_q8hnaxs', // 🔹 Replace with your EmailJS service ID
        'template_rcus0om', // 🔹 Replace with your EmailJS template ID
        templateParams,
        'AmbQ-WAMkvA9EFT58' // 🔹 Replace with your EmailJS public key
      );
      setStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('EmailJS Error:', error);
      setStatus('error');
    }
  };

  return (
    <div className="mt-[6rem] flex w-full flex-wrap justify-center py-[4rem] font-inter">
      <div className="flex w-full max-w-[114rem] flex-row flex-wrap items-stretch justify-center gap-x-[1rem] gap-y-[3rem] px-[2rem]">
        <div className="flex w-full flex-wrap items-start gap-[1.5rem] md:gap-[2rem]">
          <div className="flex w-full justify-center font-inter">
            <div className="flex w-full max-w-[114rem] flex-col items-stretch justify-center gap-[4rem] px-[2rem]">
              {/* ===== HEADING ===== */}
              <div className="flex flex-col items-center text-center">
                <h1 className="pt-[4rem] text-[3.6rem] font-[600] leading-[4.65rem] text-white md:pt-[6rem] lg:pt-[10rem]">
                  Contact Us
                </h1>
                <h1 className="mb-[2rem] mt-[2rem] max-w-[70rem] text-[1.8rem] font-[400] leading-[2.4rem] text-[#828FA0]">
                  You can contact us any way that is convenient for you. We are available 24/7 via
                  fax, email or telephone.
                </h1>
              </div>{' '}
            </div>{' '}
          </div>

          <div className="mt-[3rem] flex w-full flex-wrap items-start justify-between gap-y-[2rem]">
            {/* Left Info Card */}
            <div className="mt-[10rem] w-full pr-[2rem] md:w-[calc(100%-57rem)] lg:mt-[10rem]">
              <h1 className="text-[1.6rem] font-[600] leading-[2.6rem] text-[#0EC9AC] lg:text-[1.8rem] lg:leading-[2.8rem]">
                Contact Us
              </h1>

              <h1 className="mt-[1rem] text-[2.4rem] font-[600] leading-[3.2rem] text-white lg:text-[3.6rem] lg:leading-[4.6rem]">
                Get In Touch
              </h1>

              <div className="mt-[4rem] flex flex-row flex-wrap items-center justify-between gap-[4rem]">
                <div className="flex flex-row items-stretch justify-start gap-[1.5rem] md:gap-[2rem]">
                  <div className="flex h-[5rem] w-[5rem] items-center justify-center rounded-[1rem] bg-[#011936] text-white">
                    <MapIcon />
                  </div>

                  <div className="mt-[0.4rem] flex w-[calc(100%-6rem)] flex-col gap-[1.5rem]">
                    <h1 className="text-[1.6rem] font-[500] leading-[100%] text-white">
                      Head Office
                    </h1>
                    <p className="text-[1.6rem] font-[400] leading-[2.4rem] text-[#828FA0]">
                      {details?.address}
                    </p>
                  </div>
                </div>
                <div className="flex flex-row items-stretch justify-start gap-[1.5rem] md:gap-[2rem]">
                  <div className="flex h-[5rem] w-[5rem] items-center justify-center rounded-[1rem] bg-[#011936] text-white">
                    <PhoneCallIcon />
                  </div>

                  <div className="mt-[0.4rem] flex flex-col gap-[1.5rem]">
                    <h1 className="text-[1.8rem] font-[500] leading-[100%] text-white">Call Us</h1>
                    <p className="text-[1.6rem] font-[400] leading-[2.4rem] text-[#828FA0]">
                      {details?.phone}
                    </p>
                  </div>
                </div>
                <div className="flex flex-row items-stretch justify-start gap-[1.5rem] md:gap-[2rem]">
                  <div className="flex h-[5rem] w-[5rem] items-center justify-center rounded-[1rem] bg-[#011936] text-white">
                    <MailIcon />
                  </div>

                  <div className="mt-[0.4rem] flex flex-col gap-[1.5rem]">
                    <h1 className="text-[1.8rem] font-[500] leading-[100%] text-white">
                      Email Address
                    </h1>
                    <p className="text-[1.6rem] font-[400] leading-[2.4rem] text-[#828FA0]">
                      {details?.email}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Section */}
            <div className="flex w-full flex-wrap items-start justify-between rounded-[1rem] border border-[#10233B] px-[4rem] py-[4rem] md:w-[55rem]">
              <div className="flex w-full flex-row flex-wrap items-center justify-between gap-y-[2rem]">
                <input
                  placeholder="Name"
                  value={formData.name}
                  onChange={handleChange}
                  className="text-footer-bottom-header-content h-none w-full appearance-none rounded-md bg-[#011936] px-[1rem] py-[1.5rem] text-[1.6rem] text-white outline-none"
                />
                <input
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleChange}
                  className="text-footer-bottom-header-content h-none w-full appearance-none rounded-md bg-[#011936] px-[1rem] py-[1.5rem] text-[1.6rem] text-white outline-none"
                />
                <input
                  placeholder="Subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className="text-footer-bottom-header-content h-none w-full appearance-none rounded-md bg-[#011936] px-[1rem] py-[1.5rem] text-[1.6rem] text-white outline-none"
                />
                <textarea
                  placeholder="Message"
                  value={formData.message}
                  onChange={handleChange}
                  className="text-footer-bottom-header-content h-none mt-[1.5rem] h-[15rem] w-full appearance-none rounded-md bg-[#011936] px-[1rem] py-[1.5rem] text-[1.6rem] text-white outline-none"
                />
                <div
                  onClick={status !== 'sending' ? handleSend : undefined}
                  className={`relative mt-[3rem] w-full cursor-pointer rounded-md ${
                    status === 'success'
                      ? 'bg-green-500'
                      : status === 'error'
                        ? 'bg-red-500'
                        : 'bg-[#0EC9AC]'
                  } pb-[1.5rem] pt-[1.5rem] transition-all duration-300 md:mt-[5.3rem]`}
                >
                  <h1 className="w-full text-center text-[1.6rem] font-[600] text-white md:text-[1.8rem]">
                    {status === 'sending'
                      ? 'Sending...'
                      : status === 'success'
                        ? 'Message Sent!'
                        : status === 'error'
                          ? 'Error! Try Again'
                          : 'Let’s Talk'}
                  </h1>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        className="mt-[4rem] aspect-[1.5] md:aspect-[3]"
        style={{ width: '100%', overflow: 'hidden' }}
      >
        <iframe
          title="Next-G Services Location"
          width="100%"
          height="100%"
          frameBorder="0"
          style={{ border: 0 }}
          allowFullScreen
          src="https://www.google.com/maps?q=GWG3%2B78+London,+United+Kingdom&output=embed"
        ></iframe>
      </div>
    </div>
  );
};
